import { useEffect, useState, type FormEvent } from 'react';
import { BrandMark } from '../components/BrandMark';
import { EmptyState, ErrorNotice, Loading } from '../components/Feedback';
import { SenoIcon } from '../components/SenoIcon';
import { get, post } from '../lib/api';
import type { Project, User } from '../types';

type License = {
  id: string;
  status: string;
  plan_code: 'demo' | 'journal' | 'thesis';
  name: string;
  expires_at: string | null;
  main_project_limit: number;
  derived_journal_limit: number;
  assigned_projects: number;
  assigned_main_projects: number;
  assigned_derived_journals: number;
  available_main_slots: number;
  available_derived_slots: number;
};

export function Dashboard({
  user,
  navigate,
  onLogout
}: {
  user: User;
  navigate: (path: string) => void;
  onLogout: () => void;
}) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [projectType, setProjectType] = useState<'thesis' | 'journal'>('thesis');
  const [licenses, setLicenses] = useState<License[]>([]);
  const [sourceProjectId, setSourceProjectId] = useState('');

  useEffect(() => {
    Promise.all([
      get<{ projects: Project[] }>('/api/projects'),
      get<{ licenses: License[] }>('/api/account/licenses')
    ])
      .then(([data, owned]) => {
        setProjects(data.projects);
        setLicenses(owned.licenses);
      })
      .catch((caught) =>
        setError(caught instanceof Error ? caught.message : 'Proyek tidak dapat dimuat.')
      )
      .finally(() => setLoading(false));
  }, []);

  const activeLicenses = licenses.filter(
    (license) =>
      license.status === 'active' &&
      (!license.expires_at || new Date(license.expires_at).getTime() > Date.now())
  );
  const demoAvailable = activeLicenses.some(
    (license) => license.plan_code === 'demo' && license.available_main_slots > 0
  );
  const eligibleLicenses = activeLicenses.filter((license) => {
    if (projectType === 'thesis')
      return license.plan_code === 'thesis' && license.available_main_slots > 0;
    if (sourceProjectId)
      return license.plan_code === 'thesis' && license.available_derived_slots > 0;
    return (
      ['journal', 'thesis'].includes(license.plan_code) && license.available_main_slots > 0
    );
  });

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    const form = new FormData(event.currentTarget);
    try {
      const licenseId = String(form.get('licenseId') ?? '');
      if (licenseId && !eligibleLicenses.some((license) => license.id === licenseId))
        throw new Error('Lisensi yang dipilih sudah tidak memiliki slot yang sesuai.');
      const data = await post<{ project: Project }>('/api/projects', {
        type: projectType,
        name: form.get('name'),
        ...(form.get('licenseId') ? { licenseId: form.get('licenseId') } : {}),
        ...(form.get('sourceProjectId') ? { sourceProjectId: form.get('sourceProjectId') } : {})
      });
      navigate(`/app/project/${data.project.id}/overview`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Proyek belum dapat dibuat.');
    }
  }

  return (
    <div className="dashboard-page">
      <header className="dashboard-header">
        <BrandMark />
        <div className="header-actions">
          <span className="avatar" aria-hidden="true">
            {user.displayName.slice(0, 1).toUpperCase()}
          </span>
          <div>
            <strong>{user.displayName}</strong>
            <small>{user.email}</small>
          </div>
          <button className="button button-ghost" onClick={() => navigate('/app/plans')}>
            <SenoIcon name="payment" /> Paket
          </button>
          <button className="button button-ghost" onClick={() => navigate('/app/account')}>
            <SenoIcon name="security" /> Akun
          </button>
          {user.role === 'admin' ? (
            <button className="button button-ghost" onClick={() => navigate('/app/admin')}>
              <SenoIcon name="admin" /> Admin
            </button>
          ) : null}
          <button className="button button-ghost" onClick={onLogout}>
            Keluar
          </button>
        </div>
      </header>
      <main className="dashboard-main">
        <div className="page-heading">
          <div>
            <span className="eyebrow">Ruang kerja privat</span>
            <h1>Selamat melanjutkan, {user.displayName.split(' ')[0]}.</h1>
            <p>Pilih proyek atau mulai struktur penelitian baru.</p>
          </div>
          <button className="button button-primary" onClick={() => setCreating(true)}>
            <SenoIcon name="project-new" /> Proyek baru
          </button>
        </div>
        {error ? <ErrorNotice message={error} /> : null}
        {loading ? (
          <Loading label="Mengambil proyek Anda…" />
        ) : projects.length === 0 ? (
          <EmptyState
            title="Belum ada proyek"
            description="Mulai dari jalur skripsi atau artikel jurnal. Struktur awal akan dibuat otomatis."
            action={
              <button className="button button-primary" onClick={() => setCreating(true)}>
                Buat proyek pertama
              </button>
            }
          />
        ) : (
          <div className="project-grid">
            {projects.map((project) => (
              <button
                className="project-card"
                key={project.id}
                onClick={() => navigate(`/app/project/${project.id}/overview`)}
              >
                <span className={`project-icon ${project.type}`}>
                  <SenoIcon name={project.type === 'thesis' ? 'skripsi' : 'jurnal'} />
                </span>
                <span className="project-card-main">
                  <small>{project.type === 'thesis' ? 'Skripsi' : 'Artikel Jurnal'}</small>
                  <strong>{project.name}</strong>
                  <span>
                    Terakhir diperbarui {new Date(project.updatedAt).toLocaleDateString('id-ID')}
                  </span>
                </span>
                <span className="project-arrow">→</span>
              </button>
            ))}
          </div>
        )}
        <section className="foundation-note">
          <SenoIcon name="ai" />
          <div>
            <strong>Seno AI bekerja melalui usulan yang aman</strong>
            <p>
              Setiap hasil diperiksa terhadap fakta, istilah, evidence, dan sumber sebelum Anda
              memilih Terapkan.
            </p>
          </div>
        </section>
      </main>
      {creating ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setCreating(false)}>
          <section
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-project-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button className="modal-close" aria-label="Tutup" onClick={() => setCreating(false)}>
              ×
            </button>
            <span className="eyebrow">Satu keputusan awal</span>
            <h2 id="new-project-title">Apa yang ingin Anda susun?</h2>
            <div className="path-picker">
              <button
                className={projectType === 'thesis' ? 'selected' : ''}
                onClick={() => {
                  setProjectType('thesis');
                  setSourceProjectId('');
                }}
              >
                <SenoIcon name="skripsi" />
                <strong>Susun Skripsi</strong>
                <span>Bangun penelitian dari struktur awal.</span>
              </button>
              <button
                className={projectType === 'journal' ? 'selected' : ''}
                onClick={() => setProjectType('journal')}
              >
                <SenoIcon name="jurnal" />
                <strong>Buat Artikel Jurnal</strong>
                <span>Siapkan struktur artikel dari skripsi.</span>
              </button>
            </div>
            <form onSubmit={create} className="new-project-form">
              <label>
                Nama proyek
                <input
                  name="name"
                  required
                  minLength={3}
                  maxLength={160}
                  autoFocus
                  placeholder={
                    projectType === 'thesis'
                      ? 'Contoh: Skripsi Manajemen Pemasaran'
                      : 'Contoh: Artikel Jurnal Pemasaran'
                  }
                />
              </label>
              <label>
                Lisensi proyek
                <select name="licenseId" defaultValue="">
                  <option value="">
                    {demoAvailable
                      ? 'Demo gratis tersedia'
                      : 'Demo gratis (jika belum digunakan)'}
                  </option>
                  {eligibleLicenses.map((license) => {
                    const remaining =
                      projectType === 'journal' && sourceProjectId
                        ? license.available_derived_slots
                        : license.available_main_slots;
                    return (
                      <option value={license.id} key={license.id}>
                        {license.name} · {remaining} slot tersisa
                      </option>
                    );
                  })}
                </select>
                {!demoAvailable && eligibleLicenses.length === 0 ? (
                  <small>
                    Demo akan dibuat otomatis jika belum pernah digunakan. Jika Demo sudah digunakan,
                    buka Paket untuk membuat proyek berikutnya.
                  </small>
                ) : null}
              </label>
              {projectType === 'journal' &&
              projects.some((project) => project.type === 'thesis') ? (
                <label>
                  Penelitian asal (untuk jurnal turunan)
                  <select
                    name="sourceProjectId"
                    value={sourceProjectId}
                    onChange={(event) => setSourceProjectId(event.target.value)}
                  >
                    <option value="">Artikel jurnal mandiri</option>
                    {projects
                      .filter((project) => project.type === 'thesis')
                      .map((project) => (
                        <option value={project.id} key={project.id}>
                          {project.name}
                        </option>
                      ))}
                  </select>
                </label>
              ) : null}
              <button className="button button-primary button-wide">Buat dan lanjutkan</button>
              <button
                type="button"
                className="button button-ghost button-wide"
                onClick={() => navigate('/app/plans')}
              >
                Lihat paket proyek
              </button>
            </form>
          </section>
        </div>
      ) : null}
    </div>
  );
}
